import { useTranslation } from 'react-i18next';
import React, { useEffect, useRef, useState } from 'react';
import colors from 'assets/styles/colors';

import { fields } from '../constants';
import s from './styles.module.scss';
import { getQuillDiffHTML } from '../helpers';

import type { RunbookLog } from 'infrastructure/classes/runbook-log';

interface RunbookDiffProps {
  oldData?: RunbookLog;
  newData?: RunbookLog;
  current: boolean;
}

type Marker = {
  top: number;
  el: Element;
  height: number;
};

const HEIGHT_SCALE = 0.3;
const MIN_MARKER_HEIGHT = 3;
const CONTAINER_PADDING_HEIGHT = 100;
const MINIMAP_HEIGHT = 660 - CONTAINER_PADDING_HEIGHT;
const ADD_SPAN_SELECTOR = `span[style*="color: ${colors.FOREST_GREEN}"][style*="background-color: ${colors.green0}"]`;
const DELETE_SPAN_SELECTOR = `span[style*="color: ${colors.ROCKIN_RED}"][style*="background-color: ${colors.red0}"]`;

export const RunbookDiff: React.FC<RunbookDiffProps> = ({
  oldData,
  newData,
  current,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [markers, setMarkers] = useState<Marker[]>([]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const selector = current ? ADD_SPAN_SELECTOR : DELETE_SPAN_SELECTOR;

    const changes = container.querySelectorAll(selector);
    const containerHeight = container.scrollHeight || 1;
    const totalHeight =
      containerHeight < MINIMAP_HEIGHT ? MINIMAP_HEIGHT : containerHeight;

    const marks = Array.from(changes)
      .filter((el): el is HTMLElement => el instanceof HTMLElement)
      .map((el) => {
        const top = (el.offsetTop / totalHeight) * MINIMAP_HEIGHT;
        const height = Math.max(
          (el.offsetHeight / totalHeight) * MINIMAP_HEIGHT * HEIGHT_SCALE,
          MIN_MARKER_HEIGHT,
        );

        return {
          top,
          height,
          el,
        };
      });

    setMarkers(marks);
  }, [oldData?.createdAt]);

  return (
    <div className={s['raw-html']} data-cy="diff-wrapper" ref={containerRef}>
      {fields.map(({ label, key }) => {
        const oldVal = oldData?.[key] ?? '';
        const newVal = newData?.[key] ?? '';
        const diff = getQuillDiffHTML(oldVal, newVal, current);

        return (
          <div key={key}>
            <p className={s['run-book-label']}>{t(`labels.${label}`)}</p>
            <p
              className={s['run-book-value']}
              dangerouslySetInnerHTML={{
                __html: diff,
              }}
            />
          </div>
        );
      })}
      <div
        className={s.minimap}
        style={{ right: current ? 'calc(50% + 1px)' : 0 }}
      >
        {markers.map((m, i) => (
          <button
            key={i}
            style={{
              position: 'absolute',
              top: m.top,
              height: m.height,
              left: 0,
              right: 0,
              background: current ? colors.green10 : colors.red10,
              cursor: 'pointer',
            }}
            className={s.marker}
            onClick={() =>
              m.el.scrollIntoView({ behavior: 'smooth', block: 'center' })
            }
            aria-label={`Jump to change ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
};
